import { type FC, useEffect, useContext } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';

import { GetHelpZendeskTopicsGroup } from './getHelpZendeskTopicGroups/GetHelpZendeskTopicsGroup.tsx';
import { GetHelpZendeskGroupTopicLink } from './getHelpZendeskTopicGroups/GetHelpZendeskGroupTopicLink.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { ACTIONS } from './GetHelpZendeskConstants.ts';

import { type GetHelpZendeskModalCategory_viewer$key } from './__generated__/GetHelpZendeskModalCategory_viewer.graphql.ts';

const viewerFragment = graphql`
    fragment GetHelpZendeskModalCategory_viewer on Viewer
    @refetchable(queryName: "GetHelpZendeskModalCategoryRefetchQuery") {
        zendeskSupport(isSeller: $isSeller, sellerId: $sellerId) {
            sections(categoryId: $categoryId) {
                serviceId
                displayName
                articles(isSeller: $isSeller) {
                    serviceId
                    title
                }
            }
        }
    }
`;

const GetHelpZendeskModalCategory: FC<{ viewer: GetHelpZendeskModalCategory_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const {
        dispatch,
        state: { categoryId },
    } = useContext(GetHelpZendeskContext);

    const [data, refetch] = useRefetchableFragment(viewerFragment, viewerRef);

    useEffect(() => {
        if (categoryId) {
            refetch({ categoryId });
        }
    }, [categoryId, refetch]);

    const { zendeskSupport } = data;
    const { sections } = zendeskSupport || {};

    if (!sections?.length) {
        return null;
    }

    const sectionsWithArticles = sections.filter(section => section?.articles?.length);

    return (
        <>
            {sectionsWithArticles.map(section => (
                <GetHelpZendeskTopicsGroup
                    key={section?.serviceId}
                    groupLabel={section?.displayName || ''}
                >
                    {section?.articles?.map(article => (
                        <GetHelpZendeskGroupTopicLink
                            key={article?.serviceId}
                            onClick={() => {
                                dispatch({
                                    type: ACTIONS.OPEN_ARTICLE,
                                    articleId: article?.serviceId || '',
                                });
                            }}
                        >
                            {article?.title}
                        </GetHelpZendeskGroupTopicLink>
                    ))}
                </GetHelpZendeskTopicsGroup>
            ))}
        </>
    );
};

export { GetHelpZendeskModalCategory };
